import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { useObservableState } from 'observable-hooks'
import { debounceTime } from 'rxjs/operators'
import { FieldErrors } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { DirectoTabHandle } from './DirectoTab'
import useDeclaration from './useDeclaration'
import { Declaration, TransitEditorAction } from '../../models/types'
import useCustomsActions from './useCustomsActions'
import useDownloadActions from './useDownloadActions'
import useDeclarationGrossMassValidation from './useDeclarationGrossMassValidation'
import { tryCatchAsync } from '../../../../helpers/try-catch'
import applyFormFieldErrors from '../../../../shared/rhf/applyFormFieldErrors'
import validateWithHandleSubmit from '../../../../shared/rhf/validateWithHandleSubmit'
import mapFieldErrorsToNctsErrors from '../transit/models/mapFieldErrorsToNctsErrors'
import { NctsError } from '../../../../shared/ncts/types'
import useNctsPhase from './useNctsPhase'
import useTransitOperationForm from '../../transit-operation/components/useTransitOperationForm'
import { useNctsErrors } from '../contexts/NctsErrorContext'
import { useDeclarationEditorContext } from '../contexts/DeclarationEditorContext'
import { GrossMassValidationResult } from '../../consignment/models/request'
import useScopedServerRequirements from '../validations/useScopedServerRequirements'
import { DirectoEventSaveResult } from '../directo-event/components/DirectoForm'

const customsActions: TransitEditorAction[] = ['submit', 'amend', 'cancel', 'presentationNotification', 'ensPresentationNotification']
const downloadActions: TransitEditorAction[] = ['generateXml', 'taxesPdf', 'taxesXlsx', 'pdf']

const isCustomsAction = (action: TransitEditorAction) => customsActions.includes(action)
const isDownloadAction = (action: TransitEditorAction) => downloadActions.includes(action)

export type TransitApiErrorReason =
    | 'general'
    | 'constraintViolation'
    | 'nctsError'
    | 'requiredGoodsDocumentsNotUploaded'

export type FieldError = {
  field: string
  reason: string
  message: string
}

type SaveDraftErrorReason = 'formErrors' | 'draftSaveFailed' | 'apiError'

type SaveDraftResult =
    | {
      success: true
      transit: Declaration
      directoEventSaveResult?: DirectoEventSaveResult | null
    } | {
      success: false
      errorReason: SaveDraftErrorReason
      transit?: Declaration
      formErrors?: FieldErrors<TransitOperationFormValues>
      error?: Error
      directoEventSaveResult?: DirectoEventSaveResult | null
    }

export type FormActionErrorReason =
    SaveDraftErrorReason
    | 'nctsValidationError'
    | 'goodsIssues'
    | 'apiError'
    | 'downloadError'
    | 'invalidState'
    | 'userCancel'

export type FormActionResult = {
  success: true
  transit: Declaration
  directoEventSaveResult?: DirectoEventSaveResult | null
} | {
  success: false
  errorReason: FormActionErrorReason
  transit?: Declaration
  nctsErrors?: NctsError[]
  error?: Error
  directoEventSaveResult?: DirectoEventSaveResult | null
}

export type DeclarationFormValidationResult = {
  success: true
  data: TransitOperationFormValues
  directoEventSaveResult?: DirectoEventSaveResult | null
} | {
  success: false
  formErrors?: FieldErrors<TransitOperationFormValues>
  directoEventSaveResult?: DirectoEventSaveResult | null
}

type ValidationFn = () => Promise<DeclarationFormValidationResult>

interface UseTransitFormOptions {
  isEns: boolean
  checkGrossMass: (validationResult: GrossMassValidationResult) => Promise<boolean>
  requestCancellationReason: () => Promise<string>
  confirmEnsPresentationNotification: () => Promise<boolean>
  confirmRestrictionWarning: (transitOperationId: number) => Promise<boolean>
}

function collectFormNctsErrors(saveDraftResult: SaveDraftResult) {
  if (saveDraftResult.success) return []

  let nctsErrors: NctsError[] = []
  if (saveDraftResult.formErrors) {
    nctsErrors = mapFieldErrorsToNctsErrors(saveDraftResult.formErrors)
  }

  return nctsErrors
}

function useDeclarationEditor({
  isEns,
  checkGrossMass,
  requestCancellationReason,
  confirmEnsPresentationNotification,
  confirmRestrictionWarning,
}: UseTransitFormOptions) {
  const [isFormPopulated, setIsFormPopulated] = useState(false)
  const { phase, isOptIn } = useNctsPhase()

  const { editorData, setIsCustomsSubmission } = useDeclarationEditorContext()

  const { form, populateForm } = useTransitOperationForm({ isEns })

  const directoTabRef = useRef<DirectoTabHandle | null>(null)

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const isFormActionInFlightRef = useRef<boolean>(false)
  // Set after a successful save so the next populateForm resets cleanly (no keepDirtyValues),
  // letting the form mirror the server response instead of preserving stale pre-save rows.
  const afterSaveRef = useRef<boolean>(false)

  const {
    declaration, saveDeclaration, validateDeclaration, isLoading, isSubmitting: isDeclarationSubmitting,
  } = useDeclaration()

  const { validateDeclarationGrossMass } = useDeclarationGrossMassValidation()
  const { setNctsErrors } = useNctsErrors()

  const transitOperationId = declaration?.transitOperation?.id ?? null

  const { isLoading: isServerRequirementsLoading } = useScopedServerRequirements('transitOperation', {
    transitOperationId,
    phase,
    isOptIn,
  }, { enabled: isFormPopulated })

  const { invokeCustomsAction } = useCustomsActions(transitOperationId, editorData?.country ?? null)
  const { invokeDownloadAction } = useDownloadActions(transitOperationId)

  const [debouncedInteractionLock, setDebouncedInteractionLock] = useObservableState<boolean>((
    event$,
  ) => event$.pipe(debounceTime(200)), true)

  const shouldLockInteractions = isLoading || isDeclarationSubmitting || isSaving || isServerRequirementsLoading
  setDebouncedInteractionLock(shouldLockInteractions)
  const interactionsLocked = shouldLockInteractions || debouncedInteractionLock

  const validateTransitOperationForm = useCallback(async (): Promise<DeclarationFormValidationResult> => {
    const validateResult = await validateWithHandleSubmit(form)
    if (!validateResult.success) {
      // eslint-disable-next-line no-console
      console.error('Transit operation form errors', validateResult.errors)
      return { success: false, formErrors: validateResult.errors }
    }

    return { success: true, data: validateResult.data }
  }, [form])

  const validateAllForms = useCallback(async (): Promise<DeclarationFormValidationResult> => {
    const validateResult = await validateWithHandleSubmit(form)

    const formValues: TransitOperationFormValues | undefined = validateResult.success ? validateResult.data : undefined
    const formErrors: FieldErrors<TransitOperationFormValues> | undefined = !validateResult.success ? validateResult.errors : undefined
    let allSuccessful = validateResult.success

    let directoEventSaveResult = null
    if (directoTabRef.current?.isFormEnabled()) {
      directoEventSaveResult = await directoTabRef.current?.validateAndGet()
      if (directoEventSaveResult && !directoEventSaveResult.success) {
        allSuccessful = false
        // eslint-disable-next-line no-console
        console.error('Directo form  form contains errors', directoEventSaveResult.errors)
      }
    }

    if (!allSuccessful || !formValues) {
      return {
        success: false, formErrors, directoEventSaveResult,
      }
    }

    return {
      success: true, data: formValues, directoEventSaveResult,
    }
  }, [form])

  const saveFormValues = useCallback(async ({
    formValues,
    directoEventSaveResult,
    isSubmission,
  }: {
    formValues: TransitOperationFormValues
    directoEventSaveResult?: DirectoEventSaveResult | null
    isSubmission: boolean
  }): Promise<SaveDraftResult> => {
    const { data: saved, error } = await tryCatchAsync(() => saveDeclaration({
      values: formValues,
      isDraft: !isSubmission,
    }))
    if (error) {
      return {
        success: false, errorReason: 'apiError', error, directoEventSaveResult,
      }
    }

    const { declaration: result, errors } = saved

    if (errors.length || !result.transitOperation?.id) {
      return {
        success: false, errorReason: 'draftSaveFailed', transit: result, directoEventSaveResult,
      }
    }

    // Rebase the form on the just-saved server state
    populateForm(result, { keepDirtyValues: false })

    // Full save succeeded: let the next populateForm reset cleanly to the server response.
    afterSaveRef.current = true

    return {
      success: true,
      transit: result,
      directoEventSaveResult,
    }
  }, [populateForm, saveDeclaration])

  const invokeValidateAndSaveForm = useCallback(async (validateFn: ValidationFn, isSubmission: boolean): Promise<SaveDraftResult> => {
    const validationResult = await validateFn()
    if (!validationResult.success) {
      return {
        success: false,
        errorReason: 'formErrors',
        formErrors: validationResult.formErrors,
        directoEventSaveResult: validationResult.directoEventSaveResult,
      }
    }

    return saveFormValues({
      formValues: validationResult.data,
      directoEventSaveResult: validationResult.directoEventSaveResult,
      isSubmission,
    })
  }, [saveFormValues])

  const validateAndSaveDraft = useCallback(
    async (isSubmission: boolean): Promise<SaveDraftResult> => invokeValidateAndSaveForm(validateAllForms, isSubmission),
    [validateAllForms, invokeValidateAndSaveForm],
  )

  const validateAndSaveTransitOperationDraft = useCallback(
    async (): Promise<SaveDraftResult> => invokeValidateAndSaveForm(validateTransitOperationForm, false),
    [invokeValidateAndSaveForm, validateTransitOperationForm],
  )

  useEffect(() => {
    if (isFormPopulated) return

    if (declaration.transitOperation && declaration.consignment && !isDeclarationSubmitting && !isLoading) {
      populateForm(declaration, { keepDirtyValues: !afterSaveRef.current })
      afterSaveRef.current = false
      setIsFormPopulated(true)
    }
  }, [declaration, isDeclarationSubmitting, isFormPopulated, isLoading, populateForm])

  const clearTransitNctsErrors = useCallback(() => {
    form.clearErrors()
  }, [form])

  const setTransitNctsErrors = useCallback((nctsErrors: NctsError[]) => {
    applyFormFieldErrors(form, nctsErrors)
  }, [form])

  const runValidationAction = useCallback(async (): Promise<FormActionResult | null> => {
    const { errors: nctsErrors } = await validateDeclaration({ phase, isOptIn })

    clearTransitNctsErrors()
    setNctsErrors(nctsErrors)
    if (nctsErrors.length) {
      setTransitNctsErrors(nctsErrors)
      return { success: false, errorReason: 'nctsValidationError', nctsErrors }
    }
    return null
  }, [clearTransitNctsErrors, isOptIn, phase, setNctsErrors, setTransitNctsErrors, validateDeclaration])

  const runCustomsAction = useCallback(async (
    action: TransitEditorAction,
    savedTransit: Declaration,
    reason?: string,
  ): Promise<FormActionResult> => {
    const validationResult = await runValidationAction()
    if (validationResult) {
      return { success: false, errorReason: 'nctsValidationError' }
    }

    const grossMassValidationResult = await validateDeclarationGrossMass(savedTransit.consignment?.id)
    if (!grossMassValidationResult.valid) {
      const confirm = await checkGrossMass(grossMassValidationResult)
      if (!confirm) {
        return { success: false, errorReason: 'goodsIssues' }
      }
    }

    const savedTransitOperationId = savedTransit.transitOperation?.id
    if (!isEns && (action === 'submit' || action === 'amend') && savedTransitOperationId) {
      const { data: confirmed, error: restrictionCheckError } = await tryCatchAsync(
        () => confirmRestrictionWarning(savedTransitOperationId),
      )
      if (restrictionCheckError) {
        return { success: false, errorReason: 'apiError', error: restrictionCheckError }
      }
      if (!confirmed) {
        return { success: false, errorReason: 'userCancel' }
      }
    }

    if (action === 'ensPresentationNotification' && !(await confirmEnsPresentationNotification())) {
      return { success: false, errorReason: 'userCancel' }
    }

    const { data: updatedTransitOperation, error: customsError } = await tryCatchAsync(() => invokeCustomsAction(action, reason))
    if (customsError) {
      return { success: false, errorReason: 'apiError', error: customsError }
    }

    if (!updatedTransitOperation) {
      return { success: true, transit: savedTransit }
    }

    return { success: true, transit: { ...savedTransit, transitOperation: updatedTransitOperation } }
  }, [
    checkGrossMass, confirmEnsPresentationNotification, confirmRestrictionWarning, invokeCustomsAction, isEns, runValidationAction,
    validateDeclarationGrossMass,
  ])

  const runDownloadAction = useCallback(async (action: TransitEditorAction, savedTransit: Declaration): Promise<FormActionResult> => {
    const { error } = await tryCatchAsync(() => invokeDownloadAction(action))
    if (error) {
      return { success: false, errorReason: 'downloadError', error }
    }

    return { success: true, transit: savedTransit }
  }, [invokeDownloadAction])

  const invokeFormAction = useCallback(async (action: TransitEditorAction): Promise<FormActionResult> => {
    if (isFormActionInFlightRef.current || isDeclarationSubmitting || isSaving) {
      return { success: false, errorReason: 'invalidState' }
    }
    isFormActionInFlightRef.current = true

    const isSubmission = isCustomsAction(action)
    const runsFullValidation = isSubmission || action === 'validate'

    const withSubFormSaveResults = (result: FormActionResult, subFormSaveResults: {
      directoEventSaveResult?: DirectoEventSaveResult | null
    }): FormActionResult => ({
      ...result,
      ...subFormSaveResults,
    })

    try {
      let reason: string | undefined
      if (action === 'cancel') {
        reason = await requestCancellationReason()
        if (!reason) {
          return { success: false, errorReason: 'userCancel' }
        }
      }

      setIsCustomsSubmission(runsFullValidation)
      setIsSaving(true)

      const validateAndSaveDraftResult = await validateAndSaveDraft(isSubmission)
      const subFormSaveResults = {
        directoEventSaveResult: validateAndSaveDraftResult.directoEventSaveResult,
      }

      if (!validateAndSaveDraftResult.success) {
        const formNctsErrors = collectFormNctsErrors(validateAndSaveDraftResult)
        setNctsErrors(formNctsErrors)

        return withSubFormSaveResults(
          {
            success: false,
            errorReason: validateAndSaveDraftResult.errorReason ?? 'draftSaveFailed',
            transit: validateAndSaveDraftResult.transit,
            error: validateAndSaveDraftResult.error,
            nctsErrors: formNctsErrors,
          },
          subFormSaveResults,
        )
      }

      const savedTransit = validateAndSaveDraftResult.transit
      if (!savedTransit.transitOperation?.id) {
        return withSubFormSaveResults(
          {
            success: false,
            errorReason: 'draftSaveFailed',
          },
          subFormSaveResults,
        )
      }

      if (action === 'saveDraft') {
        return withSubFormSaveResults(
          {
            success: true,
            transit: savedTransit,
          },
          subFormSaveResults,
        )
      }

      if (action === 'validate') {
        const validationResult = await runValidationAction()

        const result = validationResult ?? {
          success: true,
          transit: savedTransit,
        }

        return withSubFormSaveResults(result, subFormSaveResults)
      }

      if (isCustomsAction(action)) {
        const result = await runCustomsAction(action, savedTransit, reason)

        return withSubFormSaveResults(result, subFormSaveResults)
      }

      if (isDownloadAction(action)) {
        const result = await runDownloadAction(action, savedTransit)

        return withSubFormSaveResults(result, subFormSaveResults)
      }

      return withSubFormSaveResults(
        {
          success: true,
          transit: savedTransit,
        },
        subFormSaveResults,
      )
    } finally {
      setIsCustomsSubmission(false)
      setIsSaving(false)
      isFormActionInFlightRef.current = false
    }
  }, [isSaving, isDeclarationSubmitting, requestCancellationReason, runCustomsAction,
    runDownloadAction, runValidationAction, setNctsErrors, setIsCustomsSubmission, validateAndSaveDraft])

  return {
    isLoading,
    isSubmitting: isDeclarationSubmitting || isSaving,
    transit: declaration,
    form,
    validateAndSaveTransitOperationDraft,
    directoTabRef,
    interactionsLocked,
    invokeFormAction,
  }
}

export default useDeclarationEditor
